import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { InfoIcon } from "lucide-react";
import SimpleTable from "../../../SimpleTable";
import InfoModal from "../../../../features/TraxxionProfile/sections/TimeOff/InfoModal";
import useApi from "../../../../utils/api";
import { useCompanyDateFormat } from "../../../../utils/hooks/company";
import { useVisible } from "../../../../utils/hooks/useVisible";
import { renderSettingsResourceLabel } from "../../../../utils/helpers/settings";
import { dateTimeFormat, differenceFromNow } from "../../../../utils/helpers/date";
import { cn } from "../../../../lib/utils";

const columnHelper = createColumnHelper();

const numCell = (value: any) => {
    if (typeof value === "undefined" || Number.isNaN(value)) return null;
    return <span className="tw-tabular-nums">{Number(value).toFixed(2)}</span>;
}

function TimeOff({ user }: any) {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const { authGet } = useApi();
    const { open, close, selected, visible } = useVisible();

    const query = useQuery({
        queryKey: [`/users/${user?.id}/time-off`],
        queryFn: ({ queryKey }: any) => {
            const [url] = queryKey;
            return authGet(url);
        },
        enabled: !!user?.id
    });

    const columns = useMemo(() => [
        columnHelper.accessor("plan", {
            header: t("plan"),
            cell: (info) => {
                const value = info.getValue() as any;
                if (!value) return null;
                return (
                    <span className="tw-bg-primary/10 tw-text-primary tw-text-xs tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-md tw-whitespace-nowrap">
                        {renderSettingsResourceLabel(value)}
                    </span>
                );
            },
        }),
        columnHelper.accessor("bucket.initial", {
            header: t("initial-balance"),
            cell: (info) => numCell(info.getValue()),
        }),
        columnHelper.accessor("bucket.updates", {
            header: t("added"),
            cell: (info) => numCell(info.getValue()),
        }),
        columnHelper.accessor("bucket.earned", {
            header: t("accrued"),
            cell: (info) => numCell(info.getValue()),
        }),
        columnHelper.accessor("bucket.pending", {
            header: t("pending"),
            cell: (info) => {
                const value = info.getValue();
                if (typeof value === "undefined" || Number.isNaN(value)) return null;
                const num = Number(value);
                const data: any = info.row.original;
                const hasPending = Array.isArray(data.pendingItems) && data.pendingItems.length > 0;
                return (
                    <span
                        className={cn(
                            "tw-inline-flex tw-items-center tw-gap-1 tw-tabular-nums",
                            num > 0 && "tw-text-amber-600 tw-font-medium"
                        )}
                    >
                        {num.toFixed(2)}
                        {hasPending && (
                            <InfoIcon
                                className="tw-w-3.5 tw-h-3.5 tw-cursor-pointer tw-shrink-0"
                                onClick={() => open(data.pendingItems)}
                            />
                        )}
                    </span>
                );
            },
        }),
        columnHelper.accessor("bucket.consumed", {
            header: t("taken"),
            cell: (info) => numCell(info.getValue()),
        }),
        columnHelper.accessor("bucket.available", {
            header: t("available-balance"),
            cell: (info) => {
                const value = info.getValue();
                if (typeof value === "undefined" || Number.isNaN(value)) return null;
                const num = Number(value);
                return (
                    <span
                        className={cn(
                            "tw-font-semibold tw-tabular-nums",
                            num > 0 ? "tw-text-green-600" : "tw-text-red-500"
                        )}
                    >
                        {num.toFixed(2)}
                    </span>
                );
            },
        }),
    ], [t, open]);

    const data = query?.data ?? [];

    return (
        <div className="tw-border tw-rounded-xl tw-overflow-hidden tw-flex tw-flex-col">
            <div className="tw-flex tw-items-center tw-px-5 tw-py-4 tw-border-b">
                <div>
                    <h3 className="tw-font-medium tw-text-sm">
                        {t("length-of-service")}:{" "}
                        <span className="tw-font-bold">
                            {user?.hiringDate ? dateTimeFormat(user.hiringDate, dateFormat) : "—"}
                        </span>
                    </h3>
                    <p className="tw-text-sm tw-text-muted-foreground">
                        {user?.hiringDate ? differenceFromNow(user.hiringDate) : "—"}
                    </p>
                </div>
            </div>

            <SimpleTable
                data={data}
                loading={query.isLoading}
                columns={columns}
                footer={false}
                enableRowSelection={false}
                getRowProps={undefined}
            />

            <InfoModal
                isOpen={visible}
                close={close}
                values={selected}
            />
        </div>
    );
}

export default TimeOff;
